import React, { useEffect, useState } from "react";
import { Camera, Package, Truck, CheckCircle2, ArrowRight } from "lucide-react";
import { Card } from "../../components/ui";
import { VoltarBar } from "../../components/VoltarBar";
import { api } from "../../api/client";
import { useAuth } from "../../contexts/AuthContext";

// Tela do tab "Vistoria" no bottom nav. Antes era placeholder com
// alert('Abrir camera') - bug grave. Agora redireciona contextualmente:
//
//   Freteiro com entrega 'aceita' (indo coletar) -> abre rota_freteiro
//   Freteiro com entrega 'em_transito' (com a carga) -> abre rota_freteiro
//   Cliente com pedido 'entregue' (aguardando confirmar uso) -> abre TelaPedidos
//   Cliente com pedido 'em_uso' (devolucao proxima) -> abre TelaPedidos
//   Locador com pedido 'devolvido' (aguardando vistoria) -> abre TelaPedidoLocador
//   Sem nada pendente -> mensagem clara explicando pra que serve
//
// A camera REAL e aberta no contexto certo (ModalProvaEntrega pro
// freteiro, ModalConfirmarUso pro cliente, etc).

export const TelaVistoria = ({ nav }) => {
  const { user } = useAuth();
  const [acoes, setAcoes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }
    let cancelado = false;
    (async () => {
      const lista = [];
      try {
        // FRETEIRO: busca entregas ativas
        if (user.freteiro_habilitado) {
          const r = await api.fetch("/entregas/minhas");
          if (r.ok) {
            const entregas = await r.json();
            const lst = Array.isArray(entregas) ? entregas : [];
            const ativa = lst.find(e => ["aceita", "em_transito", "coletada"].includes(e?.status));
            if (ativa) {
              const ehColeta = ativa.status === "aceita";
              lista.push({
                tipo: "freteiro",
                titulo: ehColeta ? "Coletar produto" : "Entregar produto",
                sub: ehColeta ? "Tire fotos + video no momento da coleta" : "Tire fotos + video no destino",
                cor: ehColeta ? "amber" : "emerald",
                Icon: ehColeta ? Package : CheckCircle2,
                onClick: () => nav("rota_freteiro"),
              });
            }
          }
        }

        // CLIENTE: pedidos aguardando confirmacao de uso
        const rp = await api.listarPedidos();
        const pedidos = Array.isArray(rp) ? rp : [];
        const pendentesConfirmar = pedidos.filter(p =>
          p.cliente_id === user.id && (
            p.status === "entregue" ||
            (p.status === "pago" && p.tipo_entrega === "retirada")
          )
        );
        pendentesConfirmar.forEach(p => {
          lista.push({
            tipo: "cliente_confirmar",
            titulo: `Confirmar recebimento`,
            sub: `Pedido #${p.numero || p.id?.slice(0,8)} - tire foto pra iniciar`,
            cor: "amber",
            Icon: Camera,
            onClick: () => nav("pedidos"),
          });
        });

        // LOCADOR: pedidos devolvidos aguardando vistoria
        const pendentesDevolucao = pedidos.filter(p =>
          p.locador_id === user.id && p.status === "devolvido"
        );
        pendentesDevolucao.forEach(p => {
          lista.push({
            tipo: "locador_devolucao",
            titulo: "Vistoria de devolucao",
            sub: `Pedido #${p.numero || p.id?.slice(0,8)} - confira o produto`,
            cor: "indigo",
            Icon: Truck,
            onClick: () => nav("pedidos_locador"),
          });
        });
      } catch (e) {
        console.warn("[Vistoria] erro:", e);
      }
      if (!cancelado) {
        setAcoes(lista);
        setLoading(false);
      }
    })();
    return () => { cancelado = true; };
  }, [user, nav]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <p className="text-sm text-slate-400">Verificando vistorias pendentes...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <VoltarBar nav={nav} />
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-2xl bg-orange-100 flex items-center justify-center">
            <Camera size={22} className="text-orange-600" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold text-[#12192a]">Vistoria</h1>
            <p className="text-xs text-slate-500">Fotos + video de coleta, entrega, recebimento e devolucao</p>
          </div>
        </div>

        {acoes.length === 0 ? (
          <Card className="p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
              <Camera size={26} className="text-slate-300" />
            </div>
            <p className="font-bold text-[#12192a] mb-1">Nenhuma vistoria pendente</p>
            <p className="text-xs text-slate-500 mb-4 leading-relaxed">
              Quando voce tiver um pedido aguardando confirmacao, coleta de freteiro,
              entrega ou devolucao, vai aparecer aqui.
            </p>
            <div className="bg-slate-50 rounded-xl p-3 text-left">
              <p className="text-[11px] font-bold text-slate-700 mb-2">Pra que serve a vistoria?</p>
              <ul className="space-y-1 text-[11px] text-slate-500">
                <li>- Cliente: registrar estado do bem ao receber</li>
                <li>- Freteiro: prova de coleta e entrega (3 fotos + video)</li>
                <li>- Locador: vistoria de devolucao pra liberar caucao</li>
              </ul>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {acoes.map((a, i) => {
              const corClasses = {
                amber: "bg-amber-50 border-amber-200 text-amber-700",
                emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
                indigo: "bg-indigo-50 border-indigo-200 text-indigo-700",
              }[a.cor];
              return (
                <button key={i} onClick={a.onClick}
                  className={`w-full text-left p-4 rounded-2xl border-2 ${corClasses} flex items-center gap-3 hover:shadow-md transition-all`}>
                  <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
                    <a.Icon size={22} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-[#12192a]">{a.titulo}</p>
                    <p className="text-[11px] mt-0.5">{a.sub}</p>
                  </div>
                  <ArrowRight size={18} />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
